export interface ImageNaturalSize {
    width: number
    height: number
}

function isValidImageNaturalSize(width: number, height: number) {
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
}

const HEADER_MAX_BYTES = 32768

function inferImageTypeFromUrl(url: string): string | null {
    const cleanUrl = url.split('?')[0].split('#')[0]
    const ext = cleanUrl.split('.').pop()?.toLowerCase()
    switch (ext) {
        case 'jpg': case 'jpeg': return 'image/jpeg'
        case 'png': return 'image/png'
        case 'webp': return 'image/webp'
        case 'gif': return 'image/gif'
        default: return null
    }
}

export async function readImageUrlNaturalSizeFast(url: string): Promise<ImageNaturalSize> {
    const response = await fetch(url, {
        headers: { Range: `bytes=0-${HEADER_MAX_BYTES - 1}` },
    })

    if (response.status !== 206) {
        throw new Error('Range request not supported')
    }

    const buffer = await response.arrayBuffer()
    const view = new DataView(buffer)

    const contentType = response.headers.get('Content-Type') || ''
    const type = contentType.split(';')[0].trim() || inferImageTypeFromUrl(url)
    if (!type) throw new Error('Cannot determine image type')

    const size = parseImageSizeFromHeader(view, type)
    if (!isValidImageNaturalSize(size.width, size.height)) {
        throw new Error('Invalid image size')
    }
    return size
}

function readImageUrlNaturalSizeLegacy(url: string): Promise<ImageNaturalSize> {
    return new Promise((resolve, reject) => {
        const image = new Image()

        function cleanup() {
            image.onload = null
            image.onerror = null
        }

        image.onload = () => {
            const width = image.naturalWidth
            const height = image.naturalHeight
            cleanup()

            if (!isValidImageNaturalSize(width, height)) {
                reject(new Error('图片加载异常，无法读取尺寸'))
                return
            }

            resolve({ width, height })
        }

        image.onerror = () => {
            cleanup()
            reject(new Error('图片加载异常，无法读取尺寸'))
        }

        try {
            image.src = url
        } catch {
            cleanup()
            reject(new Error('图片加载异常，无法读取尺寸'))
        }
    })
}

export function readImageUrlNaturalSize(url: string): Promise<ImageNaturalSize> {
    return readImageUrlNaturalSizeFast(url).catch(() => {
        return readImageUrlNaturalSizeLegacy(url)
    })
}

function parsePngSize(view: DataView): ImageNaturalSize {
    if (view.getUint32(0, false) !== 0x89504e47) throw new Error('Not a PNG')
    return {
        width: view.getUint32(16, false),
        height: view.getUint32(20, false),
    }
}

function parseGifSize(view: DataView): ImageNaturalSize {
    const sig = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2))
    if (sig !== 'GIF') throw new Error('Not a GIF')
    return {
        width: view.getUint16(6, true),
        height: view.getUint16(8, true),
    }
}

function parseWebpSize(view: DataView): ImageNaturalSize {
    if (view.getUint32(0, false) !== 0x52494646) throw new Error('Not a WebP')

    const format = String.fromCharCode(
        view.getUint8(12), view.getUint8(13),
        view.getUint8(14), view.getUint8(15),
    )

    if (format === 'VP8 ') {
        return {
            width: (view.getUint16(26, true) & 0x3fff) + 1,
            height: (view.getUint16(28, true) & 0x3fff) + 1,
        }
    } else if (format === 'VP8L') {
        const bits = view.getUint32(22, true)
        return {
            width: (bits & 0x3fff) + 1,
            height: ((bits >> 14) & 0x3fff) + 1,
        }
    } else if (format === 'VP8X') {
        return {
            width: (view.getUint8(24) | (view.getUint8(25) << 8) | (view.getUint8(26) << 16)) + 1,
            height: (view.getUint8(27) | (view.getUint8(28) << 8) | (view.getUint8(29) << 16)) + 1,
        }
    }
    throw new Error('Unknown WebP format')
}

function parseJpegSize(view: DataView): ImageNaturalSize {
    if (view.getUint16(0, false) !== 0xffd8) throw new Error('Not a JPEG')

    let offset = 2
    while (offset + 7 < view.byteLength) {
        const marker = view.getUint16(offset, false)
        offset += 2

        if (
            (marker >= 0xffc0 && marker <= 0xffc3) ||
            (marker >= 0xffc5 && marker <= 0xffc7) ||
            (marker >= 0xffc9 && marker <= 0xffcb) ||
            (marker >= 0xffcd && marker <= 0xffcf)
        ) {
            return {
                height: view.getUint16(offset + 3, false),
                width: view.getUint16(offset + 5, false),
            }
        }

        const segmentLength = view.getUint16(offset, false)
        offset += segmentLength
    }
    throw new Error('JPEG SOF not found in header')
}

function parseImageSizeFromHeader(view: DataView, type: string): ImageNaturalSize {
    switch (type) {
        case 'image/jpeg': return parseJpegSize(view)
        case 'image/png': return parsePngSize(view)
        case 'image/webp': return parseWebpSize(view)
        case 'image/gif': return parseGifSize(view)
        default: throw new Error(`Unsupported image type: ${type}`)
    }
}

export function readImageFileNaturalSizeFast(file: File): Promise<ImageNaturalSize> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (e) => {
            const buffer = e.target!.result as ArrayBuffer
            const view = new DataView(buffer)

            try {
                const size = parseImageSizeFromHeader(view, file.type)
                if (!isValidImageNaturalSize(size.width, size.height)) {
                    throw new Error('Invalid image size')
                }
                resolve(size)
            } catch (err) {
                reject(err)
            }
        }

        reader.onerror = () => {
            reject(new Error('图片加载异常，无法读取尺寸'))
        }

        reader.readAsArrayBuffer(file.slice(0, HEADER_MAX_BYTES))
    })
}

function readImageFileNaturalSizeLegacy(file: File): Promise<ImageNaturalSize> {
    let objectUrl = ''

    try {
        objectUrl = URL.createObjectURL(file)
    } catch {
        return Promise.reject(new Error('图片加载异常，无法上传'))
    }

    return readImageUrlNaturalSizeLegacy(objectUrl)
        .catch(() => {
            throw new Error('图片加载异常，无法上传')
        })
        .finally(() => {
            URL.revokeObjectURL(objectUrl)
        })
}

export function readImageFileNaturalSize(file: File): Promise<ImageNaturalSize> {
    return readImageFileNaturalSizeFast(file).catch(() => {
        return readImageFileNaturalSizeLegacy(file)
    })
}

export class ImageUtils {

    public static getBase64ImageFormat(base64Data: string) {
        // 检查 Base64 数据是否有效
        if (!base64Data || typeof base64Data !== 'string') {
            return null;
        }

        // 提取数据头
        const header = base64Data.split(',')[0];
        if (!header) {
            return null;
        }

        // 从数据头中获取格式
        return header.split(';')[0].split('/')[1]; // 返回格式，例如 'png', 'jpeg', 'gif' 等
    }

    public static base64ToPngFile(base64Data: string, filename: string) {
        return new Promise<File | null>((resolve) => {
            // 创建一个图像对象
            const img = new Image();
            img.onload = () => {
                // 创建一个 canvas 元素
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;

                // 将图像绘制到 canvas 上
                const ctx = canvas.getContext('2d');
                if (!ctx) return resolve(null);

                ctx.drawImage(img, 0, 0);
                // 将 canvas 转为 PNG 格式的 Base64
                canvas.toBlob((blob) => {
                    if (blob) {
                        // 创建 File 对象
                        const file = new File([blob], filename, { type: 'image/png' });
                        resolve(file); // 返回 File 对象
                    } else {
                        console.error('base64ToPngFile Blob conversion failed')
                        // reject(new Error('Blob conversion failed'));
                        resolve(null);
                    }
                }, 'image/png');
            };

            img.onerror = (error) => {
                console.error('base64ToPngFile onerror', error)
                // reject(new Error('Image loading failed: ' + error));
                resolve(null);
            };

            // 设置图像源
            img.src = base64Data;
        });
    }

}